import {useEffect, useMemo, useState} from "react";
import {Checkbox, Collapse, Tag, Tooltip, Typography} from 'antd';
import {QuestionCircleOutlined} from '@ant-design/icons';
import ApiService from "../../core/service/api-service";
import {t} from "i18next";
import {useUser} from "../../core/context/UserContext";

/**
 * Rol ve kullanıcı ekranlarının ortak izin listesi.
 *
 * savedPermissions verilirse (rol ekranı) kaydedilmiş hâlden farklılaşan izinler satırda ve grup
 * başlığında işaretlenir — kullanıcı neyin henüz kaydedilmediğini listeyi gezmeden görsün.
 * readOnly: kutuların tamamı pasif (ör. alt seviyenin düzenleyemeyeceği süper yönetici rolü).
 */
export default function PermissionGroup({selectedPermissions = [], onChangeSelectedPermissions, disabledPermission, savedPermissions, readOnly = false})
{
    const [groups, setGroups] = useState([])

    // Kimse kendisinde olmayan bir izni veremez (sunucu da reddeder). Kutu pasif çizilir ki yönetici
    // işaretleyip kaydetmeyi denemeden sınırı görsün. SUPER_ADMIN'i ise sunucu listeye hiç koymaz.
    const {ownsPermission} = useUser()

    useEffect(() => {
        (async () => {
            const response = await ApiService.PermissionGroup.GetList()
            if(response.success)
                setGroups(response.data)
        })()
    }, [])

    // İzin adı/açıklaması PERM_ önekiyle çevrilir: çıplak izin adı (ör. VOICE_RECORD_ADD) çekim
    // logu tipleriyle aynı anahtarı paylaşıyor, önek olmadan log metni izin adı diye görünürdü.
    // PERM_ karşılığı olmayan dillerde eski anahtara düşülür.
    const permissionName = (p) => t(`PERM_${p.name}`, {defaultValue: t(p.name)})
    const permissionDesc = (p) => t(`PERM_${p.name}_DESC`, {defaultValue: t(`${p.name}_DESC`)})
    const groupName = (group) => t(`PERM_GROUP_${group.id}`, {defaultValue: group.name})

    const selected = useMemo(() => new Set(selectedPermissions), [selectedPermissions])
    const fixed = useMemo(() => new Set(disabledPermission || []), [disabledPermission])
    const saved = useMemo(() => savedPermissions ? new Set(savedPermissions) : null, [savedPermissions])

    // Kullanıcıda/rolde ZATEN duran ama düzenleyende olmayan izin: pasif ama işaretli kalır ve
    // seçimden düşürülmez — dokunmadığı bir izni sessizce kaldırmış olmayalım.
    const isLocked = (permission) => !ownsPermission(permission.id)

    // fixed: kullanıcı ekranında rolden gelen izinler — işaretli görünür, tek tek değiştirilemez.
    const isToggleable = (permission) => !readOnly && !isLocked(permission) && !fixed.has(permission.id)

    const isChecked = (permission) => selected.has(permission.id) || fixed.has(permission.id)

    const changeState = (permission) => {
        if(!saved)
            return null

        const now = selected.has(permission.id)
        const before = saved.has(permission.id)

        if(now === before)
            return null

        return now ? 'added' : 'removed'
    }

    const onSelect = (data) => {
        data = data.filter(x => !fixed.has(x))
        onChangeSelectedPermissions && onChangeSelectedPermissions([...new Set(data)])
    }

    const onCheckedPermission = (checked, permission) => {
        if(!isToggleable(permission))
            return

        if(checked)
            onSelect([...selectedPermissions, permission.id])
        else
            onSelect(selectedPermissions.filter(p => p !== permission.id))
    }

    // Toplu seçim yalnız değiştirilebilen izinlere dokunur: kilitli ya da rolden gelen izinler olduğu
    // gibi kalır, tek tek tıklamayla aynı kural.
    const onCheckedGroup = (checked, group) => {
        const ids = group.permissions.filter(isToggleable).map(p => p.id)

        if(checked)
            onSelect([...selectedPermissions, ...ids])
        else
            onSelect(selectedPermissions.filter(p => !ids.includes(p)))
    }

    const renderHeader = (group) => {
        const toggleable = group.permissions.filter(isToggleable)
        const toggleableChecked = toggleable.filter(p => selected.has(p.id)).length
        const allChecked = toggleable.length > 0 && toggleableChecked === toggleable.length

        return (
            <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                {/* Başlığa tıklamak paneli açıp kapatır; kutuya tıklamak bunu tetiklememeli. */}
                <span onClick={e => e.stopPropagation()}>
                    <Tooltip title={allChecked ? t('Deselect All') : t('Select All')}>
                        <Checkbox
                            disabled={toggleable.length === 0}
                            checked={allChecked}
                            indeterminate={toggleableChecked > 0 && !allChecked}
                            onChange={e => onCheckedGroup(e.target.checked, group)}
                        />
                    </Tooltip>
                </span>
                <span style={{fontWeight: 600}}>{groupName(group)}</span>
            </div>
        )
    }

    const renderExtra = (group) => {
        const checkedCount = group.permissions.filter(isChecked).length
        const changedCount = group.permissions.filter(p => changeState(p)).length

        return (
            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                {changedCount > 0 && (
                    <Tag color={'warning'} style={{marginInlineEnd: 0}}>
                        {t('CHANGE_COUNT', {count: changedCount})}
                    </Tag>
                )}
                <Typography.Text type={'secondary'}>
                    {checkedCount} / {group.permissions.length}
                </Typography.Text>
            </div>
        )
    }

    const renderPermission = (p) => {
        const locked = isLocked(p)
        const state = changeState(p)

        return (
            <div
                key={p.id + 'pg'}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    padding: '6px 8px',
                    borderRadius: 6,
                    border: `1px solid ${state === 'added' ? '#b7eb8f' : state === 'removed' ? '#ffa39e' : 'transparent'}`,
                    background: state === 'added' ? '#f6ffed' : state === 'removed' ? '#fff1f0' : undefined,
                }}
            >
                <div style={{display: 'flex', alignItems: 'center', minWidth: 0}}>
                    <Checkbox
                        id={p.id + 'pg'}
                        onChange={(e) => onCheckedPermission(e.target.checked, p)}
                        disabled={!isToggleable(p)}
                        checked={isChecked(p)}
                    >
                        <Tooltip title={locked ? t('You cannot grant a permission you do not have yourself') : undefined}>
                            <span>{permissionName(p)}</span>
                        </Tooltip>
                    </Checkbox>
                    {/* Açıklama kutunun DIŞINDA: ikona tıklamak/üzerine gelmek izni işaretlemesin. */}
                    <Tooltip title={permissionDesc(p)}>
                        <QuestionCircleOutlined style={{color: 'var(--text-3, #8c8c8c)', cursor: 'help', flexShrink: 0}} />
                    </Tooltip>
                </div>
                {state && (
                    <Tag color={state === 'added' ? 'success' : 'error'} style={{marginInlineEnd: 0}}>
                        {state === 'added' ? t('Will be added') : t('Will be removed')}
                    </Tag>
                )}
            </div>
        )
    }

    return (
        <Collapse
            items={groups.map(group => ({
                key: group.id + 'g',
                label: renderHeader(group),
                extra: renderExtra(group),
                children: (
                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 4}}>
                        {group.permissions.map(renderPermission)}
                    </div>
                ),
            }))}
        />
    )
}
